import React from 'react';
import { format } from 'date-fns';

const AppointmentDetails = ({ 
  appointment, 
  isOpen, 
  onClose, 
  onEdit, 
  onDelete, 
  currentUser 
}) => {
  if (!isOpen || !appointment) return null;
  
  // Check if the appointment is in the past
  const isPastAppointment = new Date(appointment.end) < new Date();
  
  // Format the date and time for display
  const appointmentDate = format(new Date(appointment.start), 'PPPP'); // Monday, January 1, 2023
  const appointmentTime = `${format(new Date(appointment.start), 'h:mm a')} - ${format(new Date(appointment.end), 'h:mm a')}`;

  // Check if the current user can edit this appointment
  const canEdit = !isPastAppointment && 
    (currentUser?.role === 'admin' || 
     (currentUser?.role === 'doctor' && appointment.resource?.doctorId === currentUser?._id) || 
     (currentUser?.role === 'patient' && appointment.resource?.patientId === currentUser?._id));
  
  return (
    <div className="fixed inset-0 z-10 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={onClose}></div>

        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="sm:flex sm:items-start">
              <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                  {appointment.title || 'Appointment Details'}
                </h3>
                
                <div className="mt-4 space-y-4">
                  {/* Date and Time */}
                  <div>
                    <div className="text-sm font-medium text-gray-500">Date & Time</div>
                    <div className="mt-1">
                      <p className="text-sm text-gray-900">{appointmentDate}</p>
                      <p className="text-sm text-gray-900">{appointmentTime}</p>
                    </div>
                  </div>
                  
                  {/* Doctor Information */}
                  {appointment.resource?.doctorName && (
                    <div>
                      <div className="text-sm font-medium text-gray-500">Doctor</div>
                      <div className="mt-1">
                        <p className="text-sm text-gray-900"> {appointment.resource.doctorName}</p>
                        {appointment.resource?.specialty && (
                          <p className="text-sm text-gray-600">{appointment.resource.specialty}</p>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {/* Patient Information - only show if current user is a doctor */}
                  {currentUser?.role === 'doctor' && appointment.resource?.patientName && (
                    <div>
                      <div className="text-sm font-medium text-gray-500">Patient</div>
                      <div className="mt-1">
                        <p className="text-sm text-gray-900">{appointment.resource.patientName}</p>
                      </div>
                    </div>
                  )}
                  
                  {/* Description */}
                  {appointment.resource?.description && (
                    <div>
                      <div className="text-sm font-medium text-gray-500">Description</div>
                      <div className="mt-1">
                        <p className="text-sm text-gray-900">{appointment.resource.description}</p>
                      </div>
                    </div>
                  )}
                  
                  {/* Video Meeting Link */}
                  {appointment.resource?.meetingLink && (
                    <div>
                      <div className="text-sm font-medium text-gray-500">Video Consultation</div>
                      <div className="mt-1">
                        <a 
                          href={appointment.resource.meetingLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                          Join Meeting
                        </a>
                        {appointment.resource?.meetingPassword && (
                          <p className="text-xs text-gray-500 mt-1">
                            Password: <span className="font-medium">{appointment.resource.meetingPassword}</span>
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Status indication */}
                  <div>
                    <div className="text-sm font-medium text-gray-500">Status</div>
                    <div className="mt-1">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        isPastAppointment 
                          ? 'bg-gray-100 text-gray-800' 
                          : 'bg-green-100 text-green-800'
                      }`}>
                        {isPastAppointment ? 'Past Appointment' : 'Upcoming'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            {canEdit && (
              <>
                <button
                  type="button"
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={() => onEdit(appointment)}
                >
                  Edit
                </button>
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={() => onDelete(appointment.id)}
                >
                  Cancel Appointment
                </button>
              </>
            )}
            <button
              type="button"
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
              onClick={onClose}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AppointmentDetails;